<template>
    <div>
        <div class="sm:mb-5 panel panel-xs border border-2 border-secondary broadcast-video-viewer-panel" :class="data.isVisible ? 'visible' : 'hidden'">
            <div class="relative pb-[75%] broadcast-video-frame">
                <div class="broadcast-video-status" v-if="data.isVisible">
                <span class="broadcast-video-status-dot">
                    <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                    <span class="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                </span>
                    <span class="hidden sm:inline">Live</span>
                </div>
                <video class="broadcast-video-element"
                       ref="viewerRef"
                       playsinline
                       muted
                       @volumechange="syncAudioMutedState"></video>
                <div
                    v-if="data.showAudioPrompt"
                    class="absolute inset-0 bg-black bg-opacity-60 z-20 flex items-center justify-center rounded-md px-4">
                    <div class="relative bg-white rounded-md p-4 text-center max-w-sm shadow-lg">
                        <button
                            type="button"
                            class="absolute top-2 right-2 text-sm text-primary opacity-70 hover:opacity-100"
                            title="Dismiss audio prompt"
                            aria-label="Dismiss audio prompt"
                            @click="dismissAudioPrompt">
                            <i class="fas fa-times"></i>
                        </button>
                        <p class="font-bold mb-2">Live audio is available</p>
                        <p class="text-sm mb-4">Click below to unmute the host audio.</p>
                        <button type="button" class="btn btn-secondary text-white" @click="enableAudioFromPrompt">
                            Enable audio
                        </button>
                    </div>
                </div>
            </div>
            <p v-if="data.error">
                {{data.error}}
            </p>
            <div class="broadcast-video-controls" v-if="data.isVisible">
                <button
                    @click="toggleAudioMute"
                    class="broadcast-video-audio-button"
                    :title="data.isAudioMuted ? 'Unmute audio' : 'Mute audio'">
                    <span v-if="data.isAudioMuted"><i class="fas fa-volume-mute"></i></span>
                    <span v-else><i class="fas fa-volume-up"></i></span>
                </button>
                <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    :value="data.audioVolume"
                    class="broadcast-video-volume"
                    title="Volume"
                    aria-label="Volume"
                    @input="handleAudioVolumeInput"
                />
            </div>
        </div>
    </div>
</template>
<script setup>
import Peer from "simple-peer";
import api from "../../../api/axios-api";
import {onBeforeUnmount, onMounted, ref} from "vue";
import {useQuizStore} from "../../../stores/quiz.js";
import * as stream from "node:stream";

const quizStore = useQuizStore();

const props = defineProps(['quizId']);

const data = ref({
    clientId: null,
    streamingPresenceChannel: null,
    destroyPeerTimeOut: null,
    isLoading: false,
    broadcasterPeer: null,
    broadcasterId: null,
    isVisible: false,
    error: false,
    isAudioMuted: true,
    showAudioPrompt: false,
    audioVolume: 0,
    lastAudioVolume: 1,
    audioPromptDismissed: false,
});

const viewerRef = ref(null);


const joinBroadcast = () => {
    initializeStreamingChannel();
    initializeSignalOfferChannel(); // a private channel where the viewer listens to incoming signalling offer
}

const initializeStreamingChannel = () => {
    if (data.value.streamingPresenceChannel === null) {
        data.value.streamingPresenceChannel = Echo.join(
            `streaming-channel.${props.quizId}`
        ).leaving(() => {
            console.log('leaving');
        }).subscribed((user) => {

        });
    }
    data.value.clientId = Echo.socketId();
}

const createViewerPeer = (incomingOffer, broadcaster) => {
    console.log('creating viewer peer for', broadcaster);

    const peer = new Peer({
        initiator: false,
        trickle: false,
        debug: true
    });

    // Add Transceivers
    peer.addTransceiver("video", { direction: "recvonly" });
    peer.addTransceiver("audio", { direction: "recvonly" });

    // Initialize Peer events for connection to remote peer
    handlePeerEvents(
        peer,
        incomingOffer,
        broadcaster,
        removeBroadcastVideo
    );

    data.value.broadcasterPeer = peer;

    console.log('broadcasting peer created viewer peer for',  data.value.broadcasterPeer);
}

const handlePeerEvents = (peer, incomingOffer, broadcaster, cleanupCallback) => {
    peer.on("signal", async (stream) => {
        try {
            data.value.isLoading = true;
            await api.post(`/quiz/${props.quizId}/stream-answer`, {
                broadcaster,
                client_id: data.value.clientId,
                answer: stream,
            });
        } catch (err) {
            data.value.error = err.message;
        } finally {
            data.value.isLoading = false;
        }
    });
    peer.on("stream", (stream) => {
        if (!viewerRef.value) {
            return;
        }

        viewerRef.value.srcObject = stream;
        // Wait for video metadata to load before playing
        viewerRef.value.addEventListener("loadedmetadata", () => {
            if (viewerRef.value.readyState >= 2) {
                applyPreferredAudioState();
            } else {
                // Handle metadata loading error
                console.log("Error loading video metadata");
            }
        });

        // display remote stream
        data.value.isVisible = true;
        data.value.audioPromptDismissed = false;
        maybePromptForAudio();
    });
    peer.on("close", () => {
        console.log('Closing peer connection');
        data.value.destroyPeerTimeOut = setTimeout(() => {
            peer.destroy();
            cleanupCallback(false);
        }, 1000);
    });
    peer.on("error", (err) => {
        console.error(err);
    });
    const updatedOffer = {
        ...incomingOffer,
        sdp: `${incomingOffer.sdp}\n`,
    };
    peer.signal(updatedOffer);
}

const initializeSignalOfferChannel = () => {
    Echo.channel(`stream-signal-channel.${props.quizId}`).listen(
        "StreamOffer",
        ({ data: streamData }) => {

            if (data.value.clientId === streamData.receiver && data.value.broadcasterId === null) {
                data.value.isLoading = true;
                data.value.broadcasterId = streamData.broadcaster;
                createViewerPeer(streamData.offer, streamData.broadcaster);
            }
        }
    ).subscribed((v) => {
        console.log('viewer subscribed to participant channel');
    })
    .error((er) => {
        console.error(er);
    })
}

const removeBroadcastVideo = (leaveBroadCastChannel = true) => {
    if (data.value.broadcasterPeer) {
        data.value.broadcasterPeer.destroy(); // Disconnect the Peer connection
        data.value.broadcasterPeer = null;
    }

    if (data.value.broadcasterId !== null) {
        data.value.broadcasterId = null;
    }

    if (viewerRef.value !== null && viewerRef.value.srcObject !== null) {
        const tracks = viewerRef.value.srcObject.getTracks();
        tracks.forEach((track) => {
            track.stop();
        });
        viewerRef.value.srcObject = null;
    }

    data.value.isVisible = false;
    data.value.showAudioPrompt = false;
    data.value.audioPromptDismissed = false;
    data.value.isAudioMuted = true;
    data.value.audioVolume = 0;
    data.value.lastAudioVolume = 1;

    if (leaveBroadCastChannel) {
        console.log('leaving channel');
        Echo.leave(`stream-signal-channel.${props.quizId}`);
        Echo.leave(`streaming-channel.${props.quizId}`);
        data.value.streamingPresenceChannel = null;
    }
}

const syncAudioMutedState = () => {
    if (!viewerRef.value) {
        return;
    }

    const isMuted = viewerRef.value.muted || viewerRef.value.volume === 0;

    data.value.isAudioMuted = isMuted;

    if (isMuted) {
        data.value.audioVolume = 0;
    } else {
        data.value.audioVolume = viewerRef.value.volume;
        data.value.lastAudioVolume = viewerRef.value.volume;
    }
    quizStore.setUserPrefersMutedVideo(data.value.isAudioMuted);

    if (!data.value.isAudioMuted) {
        data.value.showAudioPrompt = false;
    }
}

const setAudioMuted = async (muted) => {
    if (!viewerRef.value) {
        return;
    }

    viewerRef.value.muted = muted;

    if (muted) {
        data.value.audioVolume = 0;
    } else {
        const volume = data.value.lastAudioVolume > 0
            ? data.value.lastAudioVolume
            : 1;

        viewerRef.value.volume = volume;
        data.value.audioVolume = volume;
    }

    syncAudioMutedState();

    try {
        await viewerRef.value.play();
    } catch (error) {
        if (!muted) {
            data.value.showAudioPrompt = true;
        }
        console.error(error);
    }
}

const toggleAudioMute = async () => {
    await setAudioMuted(!data.value.isAudioMuted);
}

const handleAudioVolumeInput = async (event) => {
    if (!viewerRef.value) {
        return;
    }

    const volume = Number(event.target.value);
    data.value.audioVolume = volume;

    if (volume > 0) {
        data.value.lastAudioVolume = volume;
        viewerRef.value.volume = volume;
        viewerRef.value.muted = false;
    } else {
        viewerRef.value.muted = true;
    }

    syncAudioMutedState();

    if (volume > 0) {
        data.value.showAudioPrompt = false;
        data.value.audioPromptDismissed = true;
        await viewerRef.value.play();
    }
}

const enableAudioFromPrompt = async () => {
    await setAudioMuted(false);

    if (!data.value.isAudioMuted) {
        data.value.showAudioPrompt = false;
    }
}

const dismissAudioPrompt = () => {
    data.value.showAudioPrompt = false;
    data.value.audioPromptDismissed = true;
}

const maybePromptForAudio = () => {
    if (!viewerRef.value || !data.value.isVisible || data.value.audioPromptDismissed) {
        return;
    }

    syncAudioMutedState();

    if (data.value.isAudioMuted) {
        data.value.showAudioPrompt = true;
    }
}

const applyPreferredAudioState = async () => {
    if (!viewerRef.value) {
        return;
    }

    await setAudioMuted(quizStore.getUserPrefersMutedVideo);

    if (viewerRef.value.muted && !data.value.audioPromptDismissed) {
        data.value.showAudioPrompt = true;
    }
}


onMounted(() => {
    joinBroadcast();
});

onBeforeUnmount(() => {
    removeBroadcastVideo();
    if (data.value.destroyPeerTimeOut) {
        clearTimeout(data.value.destroyPeerTimeOut);
    }
})

</script>
